import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

import { isNotFound } from '../../../api/call.js';
import { useRecordLastWebsite } from '../../../api/me.js';
import { LoadingArea } from '../../../components/PlaceholderPage.js';
import { WebsiteStatusContext, type WebsiteStatus } from '../../../components/WebsiteSection.js';

/**
 * Every website section sits under this layout. Opening any page in a website
 * section makes its website the last website used, which is what the landing
 * later reads (`ux.md`, Flow C, "Recording the last website used"). The
 * Account User sees nothing when it happens.
 *
 * The server checks the website is in their account and not deleted, so a
 * website from another account, a deleted one and an unknown id all answer
 * `404` and record nothing (`technical.md`, `PATCH /api/dashboard/me`).
 */
export const Route = createFileRoute('/_app/websites/$websiteId')({
  component: WebsiteLayout,
});

const websiteIdSchema = z.uuid();

function WebsiteLayout() {
  const { websiteId } = Route.useParams();
  const isWebsiteId = websiteIdSchema.safeParse(websiteId).success;
  const record = useRecordLastWebsite();
  const { mutate, isPending, isIdle, isError, error } = record;

  useEffect(() => {
    // An id that is not a UUID is not sent: the answer is already known.
    if (isWebsiteId) mutate(websiteId);
  }, [isWebsiteId, mutate, websiteId]);

  if (isWebsiteId && (isPending || isIdle)) return <LoadingArea />;

  // Any other failure leaves the placeholder showing and records nothing.
  const status: WebsiteStatus = !isWebsiteId || (isError && isNotFound(error)) ? 'not-found' : 'ok';

  return (
    <WebsiteStatusContext.Provider value={status}>
      <Outlet />
    </WebsiteStatusContext.Provider>
  );
}
